import React from "react";
import { Badge } from "@/components/ui/badge";
import { Clock, MapPin, Eye } from "lucide-react";
import { DeviceIcon } from "@/components/DeviceIcon";
import DashboardWidget from "@/components/DashboardWidget";

interface AnalyticsStats {
  userJourneys: Array<{
    sessionId: string;
    pageViews: string[];
    startTime: number;
    duration: number;
    device: string;
    location: string;
  }>;
}

interface UserJourneysWidgetProps {
  stats: AnalyticsStats | null;
}

export const UserJourneysWidget: React.FC<UserJourneysWidgetProps> = ({
  stats,
}) => {
  const formatDuration = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <DashboardWidget
      id="user-journeys"
      title="Recent User Sessions"
      headerActions={<Eye className="h-5 w-5" />}
    >
      {stats?.userJourneys?.length ? (
        <div className="space-y-4">
          {stats.userJourneys.slice(0, 8).map((journey) => {
            const deviceType = journey.device as
              | "mobile"
              | "tablet"
              | "desktop";

            return (
              <div
                key={journey.sessionId}
                className="p-4 border rounded-lg hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <DeviceIcon
                      deviceType={deviceType}
                      className="h-4 w-4"
                      showLabel={false}
                    />
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <MapPin className="h-3 w-3" />
                      <span>{journey.location}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Clock className="h-3 w-3" />
                    <span>{formatDuration(journey.duration)}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-2">
                  {journey.pageViews.slice(0, 4).map((page, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {page}
                    </Badge>
                  ))}
                  {journey.pageViews.length > 4 && (
                    <Badge variant="outline" className="text-xs">
                      +{journey.pageViews.length - 4} more
                    </Badge>
                  )}
                </div>

                <div className="text-xs text-gray-500">
                  Started: {formatDate(journey.startTime)} •{" "}
                  {journey.pageViews.length} pages viewed
                </div>
              </div>
            );
          })}

          {stats.userJourneys.length > 8 && (
            <div className="text-center pt-3 border-t">
              <div className="text-sm text-gray-600">
                Showing 8 of {stats.userJourneys.length} recent sessions
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8">
          <Eye className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No user journey data available yet</p>
          <p className="text-sm text-gray-400">
            User sessions will appear as visitors browse your site
          </p>
        </div>
      )}
    </DashboardWidget>
  );
};

export default UserJourneysWidget;
